<template>
  <div
    class="relative rounded-full flex items-center justify-center shrink-0 transition-transform duration-200"
    :class="{'loyalty-ring-pulse': state === 'done', 'scale-[1.18]': selected}"
    :style="ringStyle"
  >
    <div
      class="relative rounded-full flex items-center justify-center text-white font-bold overflow-hidden"
      :style="innerStyle"
    >
      <span v-if="!showLogo">{{ initials }}</span>
      <img
        v-if="hasLogo"
        :src="logo ?? ''"
        :alt="initials"
        class="absolute inset-0 w-full h-full object-contain p-[3px]"
        :class="{'invisible': !showLogo, 'opacity-60 grayscale': state === 'inactive'}"
        @load="onLoad"
        @error="onError"
      >
    </div>
    <span
      v-if="state === 'done'"
      class="absolute -top-[3px] -right-[3px] w-4 h-4 rounded-full bg-secondary border-[1.5px] border-white flex items-center justify-center"
    >
      <Gift :size="9" class="text-primary-dark" />
    </span>
  </div>
</template>

<script lang="ts" setup>
import {computed, toRef} from "vue";
import {Gift} from "@lucide/vue";
import {colors} from "@/theme/colors";
import {DetectionCardState} from "@/notification/components/notification-detection/loyalty-cards/detectionCardAnalysis";
import useSponsorLogo from "@/notification/composables/useSponsorLogo";

const props = withDefaults(defineProps<{
    state: DetectionCardState;
    /** Progress toward the next reward, 0-100. */
    percent: number;
    initials: string;
    gradient: [string, string];
    /** The sponsor's branding logo URL; the initials stand in when absent. */
    logo?: string | null;
    /** Outer diameter in pixels (38 in the strip, 48 in the solo variant). */
    size?: number;
    selected?: boolean;
}>(), {
    logo: null,
    size: 38,
    selected: false
});

const {hasLogo, showLogo, onLoad, onError} = useSponsorLogo(toRef(props, "logo"));

/**
 * The progress ring: gray when inactive, a conic progress arc when active,
 * a full mint gradient when a reward is ready.
 */
const ringStyle = computed(() => {
    let background = colors.gray.light;

    if (props.state === "active") {
        background = `conic-gradient(${colors.primary.DEFAULT} 0 ${props.percent}%, ${colors.gray.light} ${props.percent}% 100%)`;
    } else if (props.state === "done") {
        background = `linear-gradient(135deg, ${colors.secondary.DEFAULT}, ${colors.green.DEFAULT})`;
    }

    return {
        width: `${props.size}px`,
        height: `${props.size}px`,
        background,
        boxShadow: "0 0 0 2px #fff"
    };
});

/**
 * The inner disc: the brand gradient behind the initials, a plate behind a
 * loaded logo — white so the logo stays readable, but the usual gray for an
 * inactive card so it does not fade into the white popup.
 */
const innerStyle = computed(() => {
    const innerSize = props.size - 8;
    const inactive = props.state === "inactive";

    let background = `linear-gradient(135deg, ${props.gradient[0]}, ${props.gradient[1]})`;

    if (inactive) {
        background = colors.gray.DEFAULT;
    } else if (showLogo.value) {
        background = "#fff";
    }

    return {
        width: `${innerSize}px`,
        height: `${innerSize}px`,
        fontSize: props.size >= 44 ? "11px" : "9px",
        background
    };
});
</script>
